/**
 * Admin Service - Centralized service for admin dashboard API operations
 */

import { ApiService } from './api';

export interface AdminStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  by_role: {
    admins: number;
    clients: number;
    project_managers: number;
    developers: number;
  };
  recent_registrations: number;
  active_today: number;
  total_projects: number;
  active_projects: number;
  completed_projects: number;
  system_health: number;
}

export interface SystemConfig {
  app_name: string;
  app_url: string;
  app_timezone: string;
  max_file_upload_size: number;
  session_lifetime: number;
  password_min_length: number;
  require_password_confirmation: boolean;
  enable_user_registration: boolean;
  email_verification_required: boolean;
  maintenance_mode: boolean;
  debug_mode: boolean;
  cache_driver: string;
  queue_driver: string;
  mail_driver: string;
  backup_retention_days: number;
}

export interface CacheStats {
  total_keys: number;
  memory_usage: string;
  hits: number;
  misses: number;
  hit_ratio: string;
  uptime: string;
  connected_clients: number;
}

export interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  services: {
    database: { status: string; response_time: number };
    redis: { status: string; response_time: number };
    storage: { status: string; usage_percentage: number };
    queue: { status: string; pending_jobs: number };
  };
  uptime: string;
  memory_usage: number;
  cpu_usage: number;
}

export interface RecentActivity {
  id: string;
  type: string;
  description: string;
  user_id?: number;
  user_name?: string;
  ip_address?: string;
  created_at: string;
}

class AdminService {
  /**
   * Get admin dashboard statistics
   */
  static async getStats(): Promise<AdminStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        stats: AdminStats;
      }>('/admin/stats');
      
      return response.stats;
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw new Error('Failed to load admin statistics');
    }
  }

  /**
   * Get system configuration
   */
  static async getSystemConfig(): Promise<SystemConfig> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        config: SystemConfig;
      }>('/admin/system/config');
      
      return response.config;
    } catch (error) {
      console.error('Error fetching system config:', error);
      throw new Error('Failed to load system configuration');
    }
  }

  /**
   * Update system configuration
   */
  static async updateSystemConfig(config: SystemConfig): Promise<void> {
    try {
      await ApiService.put<{
        success: boolean;
        message: string;
      }>('/admin/system/config', config);
    } catch (error) {
      console.error('Error updating system config:', error);
      throw new Error('Failed to update system configuration');
    }
  }

  /**
   * Get cache statistics
   */
  static async getCacheStats(): Promise<CacheStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        stats: CacheStats;
      }>('/admin/system/cache/stats');
      
      return response.stats;
    } catch (error) {
      console.error('Error fetching cache stats:', error);
      throw new Error('Failed to load cache statistics');
    }
  }

  /**
   * Clear system cache
   */
  static async clearCache(): Promise<void> {
    try {
      await ApiService.post<{
        success: boolean;
        message: string;
      }>('/admin/system/cache/clear');
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw new Error('Failed to clear system cache');
    }
  }

  /**
   * Get system health status
   */
  static async getSystemHealth(): Promise<SystemHealth> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        health: SystemHealth;
      }>('/admin/system/health');
      
      return response.health;
    } catch (error) {
      console.error('Error fetching system health:', error);
      throw new Error('Failed to load system health status');
    }
  }

  /**
   * Get recent system activities
   */
  static async getRecentActivities(limit: number = 10): Promise<RecentActivity[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        activities: RecentActivity[];
      }>(`/admin/activities?limit=${limit}`);
      
      return response.activities;
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      // Return empty array as fallback
      return [];
    }
  }

  /**
   * Get dashboard overview data
   */
  static async getDashboardOverview(): Promise<{
    stats: AdminStats;
    health: SystemHealth;
    activities: RecentActivity[];
  }> {
    try {
      const [stats, health, activities] = await Promise.all([
        this.getStats(),
        this.getSystemHealth(),
        this.getRecentActivities(5)
      ]);

      return { stats, health, activities };
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      throw new Error('Failed to load dashboard overview');
    }
  }

  /**
   * Test email functionality
   */
  static async sendTestEmail(): Promise<void> {
    try {
      await ApiService.post<{
        success: boolean;
        message: string;
      }>('/admin/system/test-email');
    } catch (error) {
      console.error('Error sending test email:', error);
      throw new Error('Failed to send test email');
    }
  }
}

export default AdminService;